import { parseHtmlToPlainText } from '@copera/tiptap/parser';
import { TablesExplorer } from '@modules/board/table-explorer/TablesExplorer';
import { useTableRowCreateMutation } from '@modules/board/utils/useTableRowCreateMutation';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import type { ChatMessageListItem } from 'src/@types/chat';
import type { PopoverProps } from 'src/components/PopoverTrigger';
import { Table } from 'src/generated/graphql';
import { useLocales } from 'src/locales';

interface CreateTaskContext {
  message: ChatMessageListItem;
  onCreate: (rowId: string, boardId: string, tableId: string) => void;
}

export const CreateTaskPopover = ({
  message,
  target,
  handleClose,
  onCreate,
}: PopoverProps<CreateTaskContext>) => {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [loading, setLoading] = useState(false);

  const { createTableRow } = useTableRowCreateMutation();

  const messageText = useMemo(() => {
    const plain = parseHtmlToPlainText(message.content ?? '').trim();

    return plain || translate('shared.labels.untitled');
  }, [message.content, translate]);

  const handleSelectTable = (table: Table) => {
    setSelectedTable(table);
  };

  const handleCreateTask = async () => {
    if (!selectedTable) {
      return;
    }
    setLoading(true);
    try {
      //should i let the user create a task without a title?
      // if (!messageText) {
      //   messageText = 'untitled';
      // }

      const { data } = await createTableRow(selectedTable.board.toString(), {
        tableId: selectedTable._id,
        title: messageText,
      });

      if (!data) {
        throw new Error();
      }

      onCreate(
        data.createTableRow._id,
        selectedTable.board.toString(),
        data.createTableRow.tableId,
      );
      handleClose();
    } catch (error) {
      enqueueSnackbar(translate('collaborate.board.fail_creating_row'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TablesExplorer
      value={selectedTable}
      selectTable={handleSelectTable}
      onConfirm={handleCreateTask}
      loading={loading}
      sx={{ maxHeight: 300 }}
      open={!!target}
      anchorEl={target}
      onClose={handleClose}
    />
  );
};
